import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useAuthContext } from "./AuthContext"
import { USER_TYPES } from "common/constants"
import useApi from "modules/professor/account"
import { getSelectedGroup } from "modules/professor/groups"

export const TeacherAuthContext = createContext()

export const useTeacherAuthContext = () => {
  const context = useContext(TeacherAuthContext)
  if (context === undefined) {
    throw new Error(
      "useTeacherAuthContext must be used within a TeacherAuthProvider"
    )
  }
  return context
}

export const TeacherAuthProvider = ({ children }) => {
  const {
    userData,
    updateUserData: baseUpdateUserData,
    logout: baseLogout,
  } = useAuthContext()
  const { getUser } = useApi()
  const [selectedGroupData, setSelectedGroupData] = useState({
    groupId: -1,
    teachers: [],
    isSequentialContent: false,
  })
  const [isConfigurationCompleted, setIsConfigurationCompleted] =
    useState(false)
  const [isRestoreCompleted, setIsRestoreCompleted] = useState(false)
  const isRestoring = useRef(false)

  useEffect(() => {
    if (!userData || userData.userId <= 0) {
      setSelectedGroupData({
        groupId: -1,
        teachers: [],
        isSequentialContent: false,
      })
      setIsConfigurationCompleted(false)
      setIsRestoreCompleted(false)
      isRestoring.current = false
      return
    }
    if (userData.userTypeId === USER_TYPES.TEACHER) {
      setIsConfigurationCompleted(
        userData?.isConfigurationCompleted && !userData?.passwordUpdateRequired
      )
      if (isRestoreCompleted || isRestoring.current) {
        return
      }
      isRestoring.current = true
      const checkSession = async () => {
        const freshUser = await getUser()
        if (freshUser) {
          baseUpdateUserData({ newUserData: freshUser })
        } else {
          isRestoring.current = false
          logout()
          return
        }
        const selectedGroup = await getSelectedGroup()
        if (selectedGroup) {
          setSelectedGroupData(selectedGroup)
        }
        setIsRestoreCompleted(true)
        isRestoring.current = false
      }
      checkSession()
    }
  }, [userData])

  const logout = useCallback(() => {
    setSelectedGroupData({
      groupId: -1,
      teachers: [],
      isSequentialContent: false,
    })
    baseLogout()
  }, [baseLogout])

  const updateSelectedGroupData = useCallback(
    (newGroupData) => {
      setSelectedGroupData({
        ...selectedGroupData,
        ...newGroupData,
      })
    },
    [selectedGroupData]
  )

  const values = useMemo(
    () => ({
      selectedGroupData,
      isConfigurationCompleted,
      isRestoreCompleted,
      setIsConfigurationCompleted,
      logout,
      setSelectedGroupData: updateSelectedGroupData,
    }),
    [selectedGroupData, isConfigurationCompleted, isRestoreCompleted]
  )

  return (
    <TeacherAuthContext.Provider value={values}>
      {children}
    </TeacherAuthContext.Provider>
  )
}

export default TeacherAuthProvider
